import { serializeTransaction } from 'viem';
import { sleep } from '@injectivelabs/utils';
import { EvmChainId } from '@injectivelabs/ts-types';
import { toUtf8, TxGrpcApi } from '@injectivelabs/sdk-ts';
import { Alchemy, Network as AlchemyNetwork } from 'alchemy-sdk';
import { ErrorType, LedgerException, WalletException, GeneralException, UnspecifiedErrorCode, TransactionException, } from '@injectivelabs/exceptions';
import { WalletAction, getKeyFromRpcUrl, WalletDeviceType, BaseConcreteStrategy, DEFAULT_BASE_DERIVATION_PATH, DEFAULT_ADDRESS_SEARCH_LIMIT, DEFAULT_NUM_ADDRESSES_TO_FETCH, } from '@injectivelabs/wallet-base';
import LedgerHW from './hw/index.js';
import { loadLedgerServiceType } from './../lib.js';
import { domainHash, messageHash } from './utils.js';
import { LedgerEip1193Provider } from './Eip1193Provider.js';
export default class LedgerBase extends BaseConcreteStrategy {
    baseDerivationPath;
    derivationPathType;
    ledger;
    evmOptions;
    alchemy;
    constructor(args) {
        super(args);
        this.baseDerivationPath = DEFAULT_BASE_DERIVATION_PATH;
        this.derivationPathType = args.derivationPathType;
        this.ledger = new LedgerHW();
        this.evmOptions = args.evmOptions;
    }
    async getWalletDeviceType() {
        return Promise.resolve(WalletDeviceType.Hardware);
    }
    async enable() {
        return Promise.resolve(true);
    }
    async disconnect() {
        this.ledger = await this.ledger.refresh();
    }
    async getAddresses() {
        const { baseDerivationPath, derivationPathType } = this;
        try {
            const accountManager = await this.ledger.getAccountManager();
            const wallets = await accountManager.getWallets(baseDerivationPath, derivationPathType);
            return wallets.map((k) => k.address);
        }
        catch (e) {
            throw new LedgerException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
    }
    async getSessionOrConfirm(address) {
        return Promise.resolve(`0x${Buffer.from(`Confirmation for ${address} at time: ${Date.now()}`).toString('hex')}`);
    }
    async sendEvmTransaction(txData, args) {
        const signedTransaction = await this.signEvmTransaction(txData, args);
        try {
            const alchemy = await this.getAlchemy(args.evmChainId);
            const provider = await alchemy.config.getProvider();
            const txHash = await provider.send('eth_sendRawTransaction', [
                signedTransaction,
            ]);
            return txHash;
        }
        catch (e) {
            throw new LedgerException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SendEvmTransaction,
            });
        }
    }
    async sendTransaction(transaction, options) {
        const { endpoints, txTimeout } = options;
        if (!endpoints) {
            throw new WalletException(new Error('You have to pass endpoints.grpc within the options for using Ethereum native wallets'));
        }
        const txApi = new TxGrpcApi(endpoints.grpc);
        const response = await txApi.broadcast(transaction, { txTimeout });
        if (response.code !== 0) {
            throw new TransactionException(new Error(response.rawLog), {
                code: UnspecifiedErrorCode,
                contextCode: response.code,
                contextModule: response.codespace,
            });
        }
        return response;
    }
    async signEip712TypedData(eip712json, address) {
        const { derivationPath } = await this.getWalletForAddress(address);
        const object = JSON.parse(eip712json);
        try {
            const ledger = await this.ledger.getInstance();
            const result = await ledger.signEIP712Message(derivationPath, object);
            const combined = `${result.r}${result.s}${result.v.toString(16)}`;
            return combined.startsWith('0x') ? combined : `0x${combined}`;
        }
        catch (e) {
            const errorMessage = e.message;
            const isKnownNanoSError = errorMessage.includes('instruction not supported') ||
                errorMessage.includes('invalid status') ||
                errorMessage.includes('not supported') ||
                errorMessage.includes('INS_NOT_SUPPORTED');
            if (!isKnownNanoSError) {
                throw new LedgerException(new Error(errorMessage), {
                    code: UnspecifiedErrorCode,
                    type: ErrorType.WalletError,
                    contextModule: WalletAction.SignTransaction,
                });
            }
            try {
                const ledger = await this.ledger.getInstance();
                const result = await ledger.signEIP712HashedMessage(derivationPath, domainHash(object), messageHash(object));
                const combined = `${result.r}${result.s}${result.v.toString(16)}`;
                return combined.startsWith('0x') ? combined : `0x${combined}`;
            }
            catch (e) {
                throw new LedgerException(new Error(e.message), {
                    code: UnspecifiedErrorCode,
                    type: ErrorType.WalletError,
                    contextModule: WalletAction.SignTransaction,
                });
            }
        }
    }
    async signAminoCosmosTransaction(_transaction) {
        throw new WalletException(new Error('This wallet does not support signing Cosmos transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.SendTransaction,
        });
    }
    async signCosmosTransaction(_transaction) {
        throw new WalletException(new Error('This wallet does not support signing Cosmos transactions'), {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextModule: WalletAction.SendTransaction,
        });
    }
    async signArbitrary(signer, data) {
        try {
            const { derivationPath } = await this.getWalletForAddress(signer);
            const ledger = await this.ledger.getInstance();
            const result = await ledger.signPersonalMessage(derivationPath, Buffer.from(toUtf8(data), 'utf8').toString('hex'));
            const combined = `${result.r}${result.s}${result.v.toString(16)}`;
            return combined.startsWith('0x') ? combined : `0x${combined}`;
        }
        catch (e) {
            throw new LedgerException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignTransaction,
            });
        }
    }
    async getEthereumChainId() {
        const alchemy = await this.getAlchemy();
        const alchemyProvider = await alchemy.config.getProvider();
        return alchemyProvider.network.chainId.toString();
    }
    async getEvmTransactionReceipt(txHash, evmChainId) {
        const alchemy = await this.getAlchemy(evmChainId);
        const provider = await alchemy.config.getProvider();
        const interval = 3000;
        const maxAttempts = 10;
        let attempts = 0;
        while (attempts < maxAttempts) {
            attempts++;
            await sleep(interval);
            try {
                const receipt = await provider.send('eth_getTransactionReceipt', [
                    txHash,
                ]);
                if (receipt) {
                    return txHash;
                }
            }
            catch { }
        }
        throw new Error(`Failed to retrieve transaction receipt for txHash: ${txHash}`);
    }
    async getPubKey() {
        throw new WalletException(new Error('You can only fetch PubKey from Cosmos native wallets'));
    }
    async signEvmTransaction(txData, args) {
        const ledgerService = await loadLedgerServiceType();
        const alchemy = await this.getAlchemy(args.evmChainId);
        const chainId = parseInt(args.evmChainId.toString(), 10);
        const nonce = await alchemy.core.getTransactionCount(args.address);
        const parseHexValue = (value) => {
            if (typeof value === 'string') {
                const hexValue = value.startsWith('0x') ? value : `0x${value}`;
                return BigInt(hexValue);
            }
            return BigInt(value);
        };
        const eip1559TxData = {
            type: 'eip1559',
            chainId,
            nonce,
            to: txData.to,
            value: parseHexValue(txData.value || '0x0'),
            data: txData.data,
            gas: parseHexValue(txData.gas),
            maxFeePerGas: parseHexValue(txData.maxFeePerGas),
            maxPriorityFeePerGas: parseHexValue(txData.maxPriorityFeePerGas),
        };
        // Serialize the transaction
        const serializedTx = serializeTransaction(eip1559TxData);
        const serializedTxHex = serializedTx.slice(2); // Remove 0x prefix
        try {
            const ledger = await this.ledger.getInstance();
            const { derivationPath } = await this.getWalletForAddress(args.address);
            // Resolve transaction for Ledger display
            const resolution = await ledgerService.resolveTransaction(serializedTxHex, {}, {});
            // Sign the transaction with Ledger
            const txSig = await ledger.signTransaction(derivationPath, serializedTxHex, resolution);
            const signedTxData = {
                ...eip1559TxData,
                v: BigInt(`0x${txSig.v}`),
                r: `0x${txSig.r}`,
                s: `0x${txSig.s}`,
            };
            return serializeTransaction(signedTxData);
        }
        catch (e) {
            throw new LedgerException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.SignEvmTransaction,
            });
        }
    }
    async getWalletForAddress(address) {
        try {
            const { baseDerivationPath, derivationPathType } = this;
            const accountManager = await this.ledger.getAccountManager();
            if (!accountManager.hasWalletForAddress(address)) {
                for (let i = 0; i < DEFAULT_ADDRESS_SEARCH_LIMIT / DEFAULT_NUM_ADDRESSES_TO_FETCH; i += 1) {
                    await accountManager.getWallets(baseDerivationPath, derivationPathType);
                    if (accountManager.hasWalletForAddress(address)) {
                        return (await accountManager.getWalletForAddress(address));
                    }
                }
            }
            return (await accountManager.getWalletForAddress(address));
        }
        catch (e) {
            throw new LedgerException(new Error(e.message), {
                code: UnspecifiedErrorCode,
                type: ErrorType.WalletError,
                contextModule: WalletAction.GetAccounts,
            });
        }
    }
    async getEip1193Provider() {
        return new LedgerEip1193Provider(this.ledger, {
            chainId: this.evmOptions.evmChainId.toString(),
            derivationPath: this.baseDerivationPath,
        });
    }
    async getAlchemy(evmChainId) {
        if (this.alchemy) {
            return this.alchemy;
        }
        const options = this.evmOptions;
        const chainId = evmChainId || options.evmChainId;
        const url = options.rpcUrl || options.rpcUrls?.[chainId];
        if (!url) {
            throw new GeneralException(new Error('Please pass rpcUrl within the evmOptions'));
        }
        this.alchemy = new Alchemy({
            apiKey: getKeyFromRpcUrl(url),
            network: chainId === EvmChainId.Mainnet
                ? AlchemyNetwork.ETH_MAINNET
                : AlchemyNetwork.ETH_SEPOLIA,
        });
        return this.alchemy;
    }
}
